import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Users, UserPlus, Link2, Copy, Trash2, ShieldCheck, Loader2, LogOut,
  CheckCircle2, XCircle, Clock, Hash,
} from "lucide-react";
import Disclaimer from "@/components/Disclaimer";

export default function Family() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = params.get("invite") || localStorage.getItem("tc_pending_invite");

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [preview, setPreview] = useState(null); // accept preview {valid, guardian_name, ...}
  const [confirm, setConfirm] = useState(null); // {type, id, name}
  const [confirmInvite, setConfirmInvite] = useState(false);

  const load = useCallback(() => {
    api.get("/family/overview").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link acceptance: ?invite=<token>
  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/family/invite/lookup?token=${encodeURIComponent(inviteToken)}`)
      .then((r) => {
        setPreview({ ...r.data, token: inviteToken });
        if (r.data.valid === false) localStorage.removeItem("tc_pending_invite");
      })
      .catch((e) => { localStorage.removeItem("tc_pending_invite"); toast.error(apiErr(e)); });
  }, [inviteToken]);

  const lookupByCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (code.length < 6) { toast.error("Enter the 6-character invite code."); return; }
    setBusy(true);
    try {
      const { data: r } = await api.get(`/family/invite/lookup?code=${encodeURIComponent(code)}`);
      if (!r.valid) { toast.error(r.reason || "Invalid invitation."); setPreview(null); }
      else setPreview({ ...r, code });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doAccept = async () => {
    setBusy(true);
    try {
      const body = preview?.token ? { token: preview.token } : { code: preview?.code };
      const { data: r } = await api.post("/family/accept", body);
      toast.success(r.message || "Linked.");
      await refreshUser();
      setPreview(null); setCodeInput("");
      localStorage.removeItem("tc_pending_invite");
      if (params.get("invite")) { params.delete("invite"); setParams(params, { replace: true }); }
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doDecline = async () => {
    setBusy(true);
    try {
      const body = preview?.token ? { token: preview.token } : { code: preview?.code };
      await api.post("/family/decline", body);
      toast.success("Invitation declined.");
      setPreview(null); setCodeInput("");
      localStorage.removeItem("tc_pending_invite");
      if (params.get("invite")) { params.delete("invite"); setParams(params, { replace: true }); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const addMember = async () => {
    setBusy(true);
    try { await api.post("/family/invite", {}); toast.success("Invitation created."); setConfirmInvite(false); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    setBusy(true);
    try { await api.post("/family/invite/revoke", { invite_id: id }); toast.success("Invitation revoked."); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.type === "unlink") await api.post("/family/unlink", { member_id: confirm.id });
      if (confirm.type === "leave") await api.post("/family/leave", { guardian_id: confirm.id });
      toast.success("Family updated.");
      setConfirm(null); await refreshUser(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed — select and copy manually."),
    );
  };

  if (!data) return <div className="p-8 text-tc-muted font-mono text-sm">Loading family…</div>;

  const origin = window.location.origin;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-8" data-testid="family-page">
      <header>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Users className="w-6 h-6 text-tc-coach" /> Family
        </h1>
        <p className="text-tc-sub mt-1 text-sm">Manage the people connected to your TRADECRAFT account. Linking is separate from your subscription and never changes anyone's learning progress.</p>
      </header>

      {/* Acceptance preview (from code entry or invite link) */}
      {preview && (
        <div className="tc-card p-6 border-tc-coach/40" data-testid="family-accept-card">
          {preview.valid === false ? (
            <div className="text-tc-bear text-sm flex items-center gap-2"><XCircle className="w-4 h-4" /> {preview.reason || "This invitation is invalid or expired."}</div>
          ) : (
            <>
              <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-1">Family invitation</div>
              <h2 className="font-heading text-xl font-bold">Join {preview.guardian_name}'s family?</h2>
              <p className="text-sm text-tc-sub mt-2">
                Accepting links <span className="font-semibold text-tc-txt">this account</span> to {preview.guardian_name}. As your guardian they'll be able to
                view your learning progress and competency, manage your safe simulation settings, and authorize advanced AI capabilities. They cannot see your password and this does not change your XP, skills or history.
              </p>
              <div className="mt-3 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-xs font-mono text-tc-sub" data-testid="family-accept-identity">
                You are signed in as <span className="text-tc-txt">{preview.you_are?.name}</span> ({preview.you_are?.email}). Only this account will be linked.
              </div>
              {preview.is_self && <div className="mt-3 text-tc-bear text-sm flex items-center gap-2"><XCircle className="w-4 h-4" /> This is your own invitation — you can't join your own family.</div>}
              {preview.already_linked && <div className="mt-3 text-tc-mastery text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> You are already part of this family.</div>}
              <div className="flex gap-3 mt-4">
                <button data-testid="family-accept-btn" disabled={busy || preview.is_self || preview.already_linked} onClick={doAccept}
                  className="flex-1 rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 text-sm hover:bg-tc-coach/90 disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Accept
                </button>
                <button data-testid="family-decline-btn" disabled={busy} onClick={doDecline}
                  className="flex-1 rounded-lg bg-white/5 border border-white/10 text-tc-sub font-semibold py-2.5 text-sm hover:border-white/30 disabled:opacity-50">
                  Decline
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* MY FAMILY (linked members — guardian side) */}
      {data.members?.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">My family</h2>
          {data.members.map((m) => (
            <div key={m.id} className="tc-card p-4 flex items-center justify-between" data-testid={`family-member-${m.id}`}>
              <div>
                <div className="font-semibold">{m.name}</div>
                <div className="text-xs text-tc-bull font-mono flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Linked{m.is_minor ? " · minor" : ""}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`family-view-progress-${m.id}`} onClick={() => navigate("/parent")}
                  className="text-xs font-mono px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-tc-sub hover:border-white/30">View Progress</button>
                <button data-testid={`family-remove-${m.id}`} onClick={() => setConfirm({ type: "unlink", id: m.id, name: m.name })}
                  className="text-xs font-mono px-3 py-1.5 rounded-lg bg-tc-bear/10 border border-tc-bear/30 text-tc-bear hover:bg-tc-bear/20 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ADD FAMILY MEMBER (adult / guardian only) */}
      {data.can_invite && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Add family member</h2>
            <button data-testid="family-add-btn" disabled={busy} onClick={() => setConfirmInvite(true)}
              className="rounded-lg bg-tc-coach/15 border border-tc-coach/40 text-tc-coach px-4 py-2 text-sm font-medium hover:bg-tc-coach/25 disabled:opacity-50 flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Add family member
            </button>
          </div>
          <p className="text-xs text-tc-muted">
            The person you're adding needs their own TRADECRAFT account. Send them the link or code below; they accept while signed into their account.
            {!data.email_available && " Email invitations are not currently available — use the secure link or code."}
          </p>

          {data.pending_invites?.length > 0 && (
            <div className="space-y-3" data-testid="family-pending">
              <h3 className="text-[11px] font-mono uppercase tracking-widest text-tc-muted pt-2">Pending invitations</h3>
              {data.pending_invites.map((inv) => (
                <div key={inv.id} className="tc-card p-4 space-y-3" data-testid={`family-invite-${inv.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs text-tc-muted font-mono flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> Expires {new Date(inv.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </div>
                    <button data-testid={`family-revoke-${inv.id}`} disabled={busy} onClick={() => revoke(inv.id)}
                      className="text-xs font-mono text-tc-bear hover:underline flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Revoke</button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-mono uppercase text-tc-muted flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> Code</span>
                    <code data-testid={`family-code-${inv.id}`} className="font-mono text-lg tracking-[0.35em] text-tc-mastery">{inv.code}</code>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`family-copy-link-${inv.id}`} onClick={() => copy(inv.link, "Invite link")}
                      className="text-xs font-mono px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-tc-sub hover:border-white/30 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Copy Link</button>
                    <button data-testid={`family-copy-code-${inv.id}`} onClick={() => copy(inv.code, "Invite code")}
                      className="text-xs font-mono px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-tc-sub hover:border-white/30 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy Code</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* MEMBER SIDE — linked guardians + join a family */}
      <section className="space-y-3">
        <h2 className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Your guardians</h2>
        {data.guardians?.length > 0 ? data.guardians.map((g) => (
          <div key={g.id} className="tc-card p-4 flex items-center justify-between" data-testid={`family-guardian-${g.id}`}>
            <div>
              <div className="font-semibold">{g.name}</div>
              <div className="text-xs text-tc-bull font-mono flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Your account is linked to this guardian</div>
            </div>
            <button data-testid={`family-leave-${g.id}`} onClick={() => setConfirm({ type: "leave", id: g.id, name: g.name })}
              className="text-xs font-mono px-3 py-1.5 rounded-lg bg-tc-bear/10 border border-tc-bear/30 text-tc-bear hover:bg-tc-bear/20 flex items-center gap-1"><LogOut className="w-3.5 h-3.5" /> Leave</button>
          </div>
        )) : <p className="text-sm text-tc-muted">You aren't linked to a guardian yet.</p>}

        <div className="tc-card p-4 mt-2">
          <h3 className="text-sm font-semibold mb-1">Join a family</h3>
          <p className="text-xs text-tc-muted mb-3">Have an invite code? Enter it to link this account to a guardian.</p>
          <div className="flex gap-2">
            <input data-testid="family-code-input" value={codeInput} maxLength={6}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder="ABC123"
              className="flex-1 min-w-0 rounded-lg bg-tc-bg border border-white/10 px-3 py-2 text-sm font-mono tracking-[0.3em] uppercase outline-none focus:border-tc-coach" />
            <button data-testid="family-code-submit" disabled={busy} onClick={lookupByCode}
              className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-5 py-2 text-sm hover:bg-tc-coach/90 disabled:opacity-50">Continue</button>
          </div>
        </div>
      </section>

      {/* Confirm dialog */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="family-confirm">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative tc-card p-6 max-w-sm w-full space-y-4">
            <h3 className="font-heading text-lg font-bold">
              {confirm.type === "unlink" ? `Remove ${confirm.name}?` : `Leave ${confirm.name}'s family?`}
            </h3>
            <p className="text-sm text-tc-sub">
              {confirm.type === "unlink"
                ? `This removes the family link and any guardian authorizations. ${confirm.name}'s account, progress, journal and history are kept intact.`
                : `This removes the link to ${confirm.name}. Your account and all your progress stay exactly as they are.`}
            </p>
            <div className="flex gap-3">
              <button data-testid="family-confirm-yes" disabled={busy} onClick={runConfirm}
                className="flex-1 rounded-lg bg-tc-bear/15 border border-tc-bear/40 text-tc-bear font-semibold py-2.5 text-sm hover:bg-tc-bear/25 disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {confirm.type === "unlink" ? "Remove" : "Leave"}
              </button>
              <button data-testid="family-confirm-no" onClick={() => setConfirm(null)}
                className="flex-1 rounded-lg bg-white/5 border border-white/10 text-tc-sub font-semibold py-2.5 text-sm hover:border-white/30">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {confirmInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="family-invite-confirm">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmInvite(false)} />
          <div className="relative tc-card p-6 max-w-sm w-full space-y-4">
            <h3 className="font-heading text-lg font-bold">Create invite?</h3>
            <p className="text-sm text-tc-sub">This creates a single-use invite link and code to share with the person you're adding.</p>
            <div className="flex gap-3">
              <button data-testid="family-invite-confirm-yes" disabled={busy} onClick={addMember}
                className="flex-1 rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 text-sm hover:bg-tc-coach/90 disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create invite
              </button>
              <button data-testid="family-invite-confirm-no" onClick={() => setConfirmInvite(false)}
                className="flex-1 rounded-lg bg-white/5 border border-white/10 text-tc-sub font-semibold py-2.5 text-sm hover:border-white/30">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <Disclaimer />
    </div>
  );
}
